// solo/JobCard.jsx
// Internal job card — materials entry + private profit summary
// Opened from the appointment view in Bookings.jsx
// Client receipt shows service + total only — never materials

import React, { useState, useEffect, useCallback } from 'react';
import api from '../axiosConfig';
import {
  Box,
  Paper,
  Typography,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Alert,
  Chip,
  CircularProgress,
  Divider,
  Tooltip,
  Snackbar,
  LinearProgress
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Save as SaveIcon,
  Close as CloseIcon,
  Lock as LockIcon,
  ShoppingCart as CartIcon,
  TrendingUp as ProfitIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material';

// ── Profit margin colour helper ───────────────────────────────
const marginColor = (pct) => {
  if (pct >= 60) return 'success';
  if (pct >= 35) return 'warning';
  return 'error';
};

// ── Private label chip ────────────────────────────────────────
const PrivateBadge = () => (
  <Chip
    icon={<LockIcon sx={{ fontSize: '12px !important' }} />}
    label="Internal only — not shown to client"
    size="small"
    variant="outlined"
    sx={{ fontSize: '10px', height: 20, borderColor: 'text.disabled', color: 'text.disabled' }}
  />
);


// ═══════════════════════════════════════════════════════════════
// JobCard component
// Props:
//   open           — boolean, controls dialog visibility
//   onClose        — function, called when dialog closes
//   appointment    — object { id, client_name, service_name,
//                             start_datetime, total_price }
//   onSaved        — optional callback after successful save
// ═══════════════════════════════════════════════════════════════
const JobCard = ({ open, onClose, appointment, onSaved }) => {

  const [products, setProducts]         = useState([]);   // available products
  const [materials, setMaterials]       = useState([]);   // current line items
  const [profit, setProfit]             = useState(null); // profit summary
  const [loading, setLoading]           = useState(false);
  const [saving, setSaving]             = useState(false);
  const [productsLoading, setProductsLoading] = useState(false);
  const [error, setError]               = useState(null);
  const [snackbar, setSnackbar]         = useState({ open: false, message: '', severity: 'success' });

  // New line item form state
  const [selectedProductId, setSelectedProductId] = useState('');
  const [selectedQty, setSelectedQty]             = useState('');

  // ── Load available products ───────────────────────────────
  const loadProducts = useCallback(async () => {
    setProductsLoading(true);
    try {
      const res = await api.get('/solo/products');
      setProducts(res.data.products || []);
    } catch (err) {
      console.error('Error loading products:', err);
      setError('Could not load products. Check your stock list is set up.');
    } finally {
      setProductsLoading(false);
    }
  }, []);

  // ── Load existing materials for this appointment ──────────
  // If none logged yet, prefill from calculator_service_details
  const loadMaterials = useCallback(async () => {
    if (!appointment?.id) return;
    setLoading(true);
    setError(null);
    try {
      const res = await api.get(`/solo/appointments/${appointment.id}/materials`);
      const existing = res.data.materials || [];

      if (existing.length > 0) {
        // Appointment already has materials logged — use them
        setMaterials(existing);
      } else {
        // No materials yet — try to prefill from calculator
        try {
          const prefill = await api.get(`/solo/appointments/${appointment.id}/materials/prefill`);
          const prefilled = prefill.data.materials || [];
          setMaterials(prefilled);
          if (prefilled.length > 0) {
            setError(null);
          }
        } catch {
          setMaterials([]); // No calculator data either — blank job card
        }
      }
    } catch (err) {
      if (err.response?.status === 404) {
        setMaterials([]);
      } else {
        console.error('Error loading materials:', err);
        setError('Could not load job card data.');
      }
    } finally {
      setLoading(false);
    }
  }, [appointment?.id]);

  // ── Load profit summary ───────────────────────────────────
  const loadProfit = useCallback(async () => {
    if (!appointment?.id) return;
    try {
      const res = await api.get(`/solo/appointments/${appointment.id}/profit`);
      setProfit(res.data);
    } catch (err) {
      // Profit summary is optional — don't block on this
      console.error('Error loading profit summary:', err);
    }
  }, [appointment?.id]);

  // ── On open ───────────────────────────────────────────────
  useEffect(() => {
    if (open && appointment?.id) {
      loadProducts();
      loadMaterials();
      loadProfit();
    }
    if (!open) {
      // Reset form state on close
      setSelectedProductId('');
      setSelectedQty('');
      setError(null);
      setProfit(null);
      setMaterials([]);
    }
  }, [open, appointment?.id, loadProducts, loadMaterials, loadProfit]);

  // ── Add a line item ───────────────────────────────────────
  const handleAddLine = () => {
    if (!selectedProductId) {
      setError('Please select a product.');
      return;
    }
    const qty = parseFloat(selectedQty);
    if (!qty || qty <= 0) {
      setError('Please enter a quantity greater than 0.');
      return;
    }

    const product = products.find(p => p.id === parseInt(selectedProductId));
    if (!product) return;

    // Prevent duplicate product on same job card
    if (materials.find(m => m.product_id === product.id)) {
      setError(`${product.name} is already on this job card. Edit the existing line instead.`);
      return;
    }

    const lineItem = {
      product_id: product.id,
      product_name: product.name,
      quantity_used: qty,
      cost_per_unit_at_time: product.cost_per_gram || 0,
      line_cost: parseFloat(((product.cost_per_gram || 0) * qty).toFixed(2))
    };

    setMaterials(prev => [...prev, lineItem]);
    setSelectedProductId('');
    setSelectedQty('');
    setError(null);
  };

  // ── Update quantity on existing line ──────────────────────
  const handleQtyChange = (productId, newQty) => {
    setMaterials(prev =>
      prev.map(m => {
        if (m.product_id !== productId) return m;
        const qty = parseFloat(newQty) || 0;
        return {
          ...m,
          quantity_used: qty,
          line_cost: parseFloat((m.cost_per_unit_at_time * qty).toFixed(2))
        };
      })
    );
  };

  // ── Remove a line ─────────────────────────────────────────
  const handleRemoveLine = (productId) => {
    setMaterials(prev => prev.filter(m => m.product_id !== productId));
  };

  // ── Save job card ─────────────────────────────────────────
  const handleSave = async () => {
    if (materials.length === 0) {
      setError('Add at least one material before saving.');
      return;
    }

    setSaving(true);
    setError(null);

    try {
      await api.post(`/solo/appointments/${appointment.id}/materials`, {
        materials: materials.map(m => ({
          product_id: m.product_id,
          quantity_used: m.quantity_used
        }))
      });

      // Reload profit after save
      await loadProfit();

      setSnackbar({
        open: true,
        message: 'Job card saved. Stock updated and shopping list recalculated.',
        severity: 'success'
      });

      if (onSaved) onSaved();

    } catch (err) {
      console.error('Error saving job card:', err);
      const msg = err.response?.data?.error || 'Could not save job card. Please try again.';
      setError(msg);
      setSnackbar({ open: true, message: msg, severity: 'error' });
    } finally {
      setSaving(false);
    }
  };

  // ── Derived totals ────────────────────────────────────────
  const totalMaterialsCost = materials.reduce((sum, m) => sum + (m.line_cost || 0), 0);
  const selectedProduct = products.find(p => p.id === parseInt(selectedProductId));

  // ── Render ────────────────────────────────────────────────
  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { borderRadius: 2 } }}
      >
        {/* ── Header ── */}
        <DialogTitle sx={{ pb: 1 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 500 }}>
                Job card
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
                {appointment?.client_name} · {appointment?.service_name}
                {appointment?.start_datetime && (
                  <> · {new Date(appointment.start_datetime).toLocaleDateString('en-GB', {
                    weekday: 'short', day: 'numeric', month: 'short'
                  })}</>
                )}
              </Typography>
            </Box>
            <IconButton onClick={onClose} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>

        <DialogContent dividers sx={{ p: 0 }}>
          {loading ? (
            <Box sx={{ p: 4, textAlign: 'center' }}>
              <CircularProgress size={32} />
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                Loading job card...
              </Typography>
            </Box>
          ) : (
            <Box>

              {/* ── Error alert ── */}
              {error && (
                <Alert severity="error" sx={{ m: 2, mb: 0 }} onClose={() => setError(null)}>
                  {error}
                </Alert>
              )}

              {/* ── Private notice ── */}
              <Box sx={{ px: 2.5, pt: 2, pb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                <PrivateBadge />
              </Box>

              {/* ── Materials table ── */}
              <Box sx={{ px: 2.5, pb: 1 }}>
                <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 500 }}>
                  Materials used
                </Typography>

                {materials.some(m => m.prefilled) && (
                  <Alert severity="info" sx={{ mb: 1.5, fontSize: '12px', py: 0.5 }}>
                    Pre-filled from your calculator. Adjust quantities if needed then save.
                  </Alert>
                )}

                {materials.length > 0 ? (
                  <Table size="small" sx={{ mb: 1.5 }}>
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 500, color: 'text.secondary', fontSize: '12px' }}>Product</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 500, color: 'text.secondary', fontSize: '12px' }}>Used (g)</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 500, color: 'text.secondary', fontSize: '12px' }}>Cost/g</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 500, color: 'text.secondary', fontSize: '12px' }}>Line cost</TableCell>
                        <TableCell />
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {materials.map(m => (
                        <TableRow key={m.product_id}>
                          <TableCell sx={{ fontSize: '13px' }}>{m.product_name}</TableCell>
                          <TableCell align="right">
                            <TextField
                              type="number"
                              value={m.quantity_used}
                              onChange={e => handleQtyChange(m.product_id, e.target.value)}
                              size="small"
                              inputProps={{ min: 0.1, step: 0.5, style: { textAlign: 'right', width: 64, fontSize: 13 } }}
                              variant="standard"
                            />
                          </TableCell>
                          <TableCell align="right" sx={{ fontSize: '13px', color: 'text.secondary' }}>
                            £{(m.cost_per_unit_at_time || 0).toFixed(4)}
                          </TableCell>
                          <TableCell align="right" sx={{ fontSize: '13px', fontWeight: 500 }}>
                            £{(m.line_cost || 0).toFixed(2)}
                          </TableCell>
                          <TableCell align="right" sx={{ py: 0 }}>
                            <IconButton size="small" onClick={() => handleRemoveLine(m.product_id)} color="error">
                              <DeleteIcon sx={{ fontSize: 16 }} />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))}
                      {/* Totals row */}
                      <TableRow>
                        <TableCell colSpan={3} sx={{ fontWeight: 500, fontSize: '13px', borderTop: '1px solid', borderColor: 'divider' }}>
                          Total materials cost
                        </TableCell>
                        <TableCell align="right" sx={{ fontWeight: 500, fontSize: '13px', borderTop: '1px solid', borderColor: 'divider' }}>
                          £{totalMaterialsCost.toFixed(2)}
                        </TableCell>
                        <TableCell sx={{ borderTop: '1px solid', borderColor: 'divider' }} />
                      </TableRow>
                    </TableBody>
                  </Table>
                ) : (
                  <Alert severity="info" sx={{ mb: 1.5, fontSize: '13px' }}>
                    No materials logged yet. Add products used below.
                  </Alert>
                )}

                {/* ── Add product row ── */}
                <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 1, borderStyle: 'dashed' }}>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                    Add a product
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start', flexWrap: 'wrap' }}>
                    <TextField
                      select
                      label="Product"
                      value={selectedProductId}
                      onChange={e => { setSelectedProductId(e.target.value); setError(null); }}
                      size="small"
                      sx={{ flex: 2, minWidth: 180 }}
                      disabled={productsLoading}
                    >
                      {productsLoading ? (
                        <MenuItem disabled>Loading...</MenuItem>
                      ) : products.length === 0 ? (
                        <MenuItem disabled>No products in stock list</MenuItem>
                      ) : (
                        products.map(p => (
                          <MenuItem key={p.id} value={p.id}>
                            {p.name}
                            {p.cost_per_gram
                              ? ` — £${parseFloat(p.cost_per_gram).toFixed(4)}/g`
                              : ' — no cost set'
                            }
                          </MenuItem>
                        ))
                      )}
                    </TextField>

                    <TextField
                      label="Grams used"
                      type="number"
                      value={selectedQty}
                      onChange={e => setSelectedQty(e.target.value)}
                      size="small"
                      sx={{ width: 110 }}
                      inputProps={{ min: 0.1, step: 0.5 }}
                      helperText={
                        selectedProduct?.default_usage_grams
                          ? `Typical: ${selectedProduct.default_usage_grams}g`
                          : null
                      }
                    />

                    <Button
                      variant="outlined"
                      startIcon={<AddIcon />}
                      onClick={handleAddLine}
                      sx={{ height: 40, whiteSpace: 'nowrap' }}
                    >
                      Add
                    </Button>
                  </Box>
                </Paper>
              </Box>

              <Divider sx={{ my: 2 }} />

              {/* ── Profit summary ── */}
              <Box sx={{ px: 2.5, pb: 2.5 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                  <ProfitIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
                  <Typography variant="subtitle2" sx={{ fontWeight: 500 }}>
                    Profit summary
                  </Typography>
                  <PrivateBadge />
                  {profit && (
                    <Tooltip title="Refresh profit calculation">
                      <IconButton size="small" onClick={loadProfit}>
                        <RefreshIcon sx={{ fontSize: 14 }} />
                      </IconButton>
                    </Tooltip>
                  )}
                </Box>

                {profit ? (
                  <Paper variant="outlined" sx={{ borderRadius: 1, overflow: 'hidden' }}>
                    <Table size="small">
                      <TableBody>
                        <TableRow>
                          <TableCell sx={{ color: 'text.secondary', fontSize: '13px' }}>Service charge</TableCell>
                          <TableCell align="right" sx={{ fontSize: '13px' }}>£{profit.service_charge?.toFixed(2)}</TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell sx={{ color: 'text.secondary', fontSize: '13px' }}>Labour (est.)</TableCell>
                          <TableCell align="right" sx={{ fontSize: '13px', color: 'error.main' }}>
                            −£{profit.labour_cost?.toFixed(2)}
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell sx={{ color: 'text.secondary', fontSize: '13px' }}>
                            Materials
                            {materials.length === 0 && (
                              <Typography variant="caption" color="warning.main" sx={{ ml: 1 }}>
                                (none logged)
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell align="right" sx={{ fontSize: '13px', color: 'error.main' }}>
                            −£{profit.materials_cost?.toFixed(2)}
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell sx={{ color: 'text.secondary', fontSize: '13px' }}>Overhead (est.)</TableCell>
                          <TableCell align="right" sx={{ fontSize: '13px', color: 'error.main' }}>
                            −£{profit.overhead_cost?.toFixed(2)}
                          </TableCell>
                        </TableRow>
                        <TableRow sx={{ backgroundColor: 'action.hover' }}>
                          <TableCell sx={{ fontWeight: 500, fontSize: '13px' }}>Gross profit</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 500, fontSize: '13px' }}>
                            £{profit.gross_profit?.toFixed(2)}
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>

                    {/* Margin bar */}
                    <Box sx={{ px: 2, py: 1.5 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                        <Typography variant="caption" color="text.secondary">Margin</Typography>
                        <Chip
                          label={`${profit.margin_pct?.toFixed(1)}%`}
                          size="small"
                          color={marginColor(profit.margin_pct)}
                          sx={{ height: 20, fontSize: '11px' }}
                        />
                      </Box>
                      <LinearProgress
                        variant="determinate"
                        value={Math.min(100, profit.margin_pct || 0)}
                        color={marginColor(profit.margin_pct)}
                        sx={{ height: 6, borderRadius: 3 }}
                      />
                      {profit.note && (
                        <Typography variant="caption" color="text.disabled" sx={{ display: 'block', mt: 0.75 }}>
                          {profit.note}
                        </Typography>
                      )}
                    </Box>
                  </Paper>
                ) : (
                  <Alert severity="info" sx={{ fontSize: '13px' }}>
                    Save materials to see the profit breakdown.
                  </Alert>
                )}
              </Box>

            </Box>
          )}
        </DialogContent>

        {/* ── Actions ── */}
        <DialogActions sx={{ px: 2.5, py: 1.5, gap: 1 }}>
          <Button onClick={onClose} variant="outlined" color="inherit">
            Close
          </Button>
          <Button
            onClick={handleSave}
            variant="contained"
            startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
            disabled={saving || materials.length === 0}
          >
            {saving ? 'Saving...' : 'Save job card'}
          </Button>
        </DialogActions>

      </Dialog>

      {/* ── Snackbar ── */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snackbar.severity} variant="filled" sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default JobCard;